import React, { Component, FunctionComponent, MouseEvent, useContext, useRef } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { convertToSqFt, ImperialSystem, MetricSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import { Redirect } from "react-router";
import PageContent from "@my-dld/components/PageContent";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { dispatch, navigate, navigateWithInParent } from "@my-dld/utils/StoreUtils";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import Switch from "@my-dld/components/Switch";
import Actions from "@my-dld/actions";
import DetailsKV from "../component/DetailsKV";
import { EjariContractStatus, EjariProperty, EjariPropertyType, ValidatePropertyRequest } from "@eres/ejari-api/Entity";
import {
	canCreateNewContract,
	canDoContractOperations,
	canDownload,
	canEditProperty,
	canHandTakeOver,
	canRenewContract,
	canTerminateContract,
} from "@eres/ejari-api/EjariPropertyUtil";
import { getLocalValue, LocaleValue } from "@eres/core/dist/LocaleValue";
import { EjariContext, IEjariContext, updateEjariContext } from "@eres/ejari-core/contexts/EjariContext";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { EjariUserType } from "@eres/ejari-core/reducers/EjariData";
import { formatAppDate } from "@my-dld/utils/Utils";
import {
	getDisplayableContract, getEjariPropertyId,
	getLastContract,
	getPropertyId,
	setPropertyId,
	validatePropertyForAction,
	validatePropertyFromApi
} from "@eres/ejari-api/Utils";
import { toFormattedCurrency } from "@eres/core/dist/Currency";
import { IEjariContractContext, resetContractContext, updateContractContext } from "@eres/ejari-core/contexts/EjariContractContext";
import { downloadTenancyContractByNumber } from "@my-dld/actions/PropertyActions";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import { getEjariActiveUserId, getEjariUserType, getManagementUserTypeId, getParticipantID, isOwnerLoggedIn } from "@10x/sso";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { EjariApi } from "@eres/ejari-api";
import { UpdateProperty } from "@eres/ejari-core/actions";
import _ from "lodash";
import PayForContract from "../component/Contract/PayForContract";
import ServiceContainer, { ServiceCard } from "@my-dld/components/ServiceContainer";
import ServiceDescriptions from "@my-dld/assets/strings/property_serv_desc";
import { LocaleUtil, StringUtil } from "@eres/core";
import { getAppServices } from "@10x/coreapp";
import { extractPropInfo } from "../component/PropertyHeader";
import { EjariServiceCode, HTRequest, HTRequestType, HTValidatePropertyRequest } from "@10x/handtakeover/api/entities";
import { getRequestDetailsPropertyId, validateProperty, validateResponse } from "@10x/handtakeover/api/api";
import { editPropertyHandTakeOver, showHandTakeOver } from "@10x/handtakeover/NavigationUtils";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { setRequest } from "@10x/handtakeover/Utils";
import { Gated } from "@10x/feature-gate";
import { PropertyCoOccupants } from "../component/CoOccupant/CoOccupants";
import { resetManagementContractContext, updateManagementContractContext } from "@eres/ejari-core/contexts/ManagementContractContext";
import { ValidateMGMContractRequest, ValidateMGMProperty } from "@eres/ejari-api/Entity/management_contract";

enum TabIDs {
	EJARI_CONTRACT_SERVICES = "ejari_contract_services",
	MGM_CONTRACT_SERVICES = "mgm_contract_services",
}

interface ITab<P> {
	id: string;
	name: LocaleValue;
	screen: FunctionComponent<P>;
}

const EjariDetailsTabs: ITab<any>[] = [
	{
		id: TabIDs.EJARI_CONTRACT_SERVICES,
		name: LVStrings.ejari_contract_details,
		screen: EjariContractTab,
	},
	{
		id: TabIDs.MGM_CONTRACT_SERVICES,
		name: LVStrings.management_contract,
		screen: ManagementContractTab,
	},
];

interface Props extends RoutedViewProps<{ propertyTypeId: string; propertyId: string }, { property: AssetCard }> {
	property?: EjariProperty;
}


interface State {
	unit: UnitSystem;
	canDownload: boolean;
	canEdit: boolean;
	canCreateNewContract: boolean;
	canRenewContract: boolean;
	canTerminateContract: boolean;
	canHandTakeOver: boolean;
	handTakeOverError?: string;
	htReqType?: HTRequestType;
	tabs: ITab<any> [];
	selectedTabId: string;
}

class PropertyDetails extends Component<Props, State> {
	static contextType = EjariContext;
	context!: React.ContextType<typeof EjariContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			unit: getAppServices().storeService.getStore().getState().User.unit,
			canDownload: false,
			canEdit: false,
			canCreateNewContract: false,
			canRenewContract: false,
			canTerminateContract: false,
			canHandTakeOver: false,
			tabs: EjariDetailsTabs,
			selectedTabId: TabIDs.EJARI_CONTRACT_SERVICES,
		};
	}

	componentDidMount() {
		if ( this.context.property ) {
			const property = this.updateFromContext();
			if ( property )
				trackEventWithData( "EjariProperty", "View", {
					type: property.PropertyTypeId,
					id: property.PropertyId
				} );
		}
	}

	headerClick = ( e: MouseEvent<HTMLDivElement> ) => {
		if ( e.shiftKey ) {
			UpdateProperty( getAppServices().storeService.getStore() );
		}
	};

	updateEjariCtx( ejariExtra?: Partial<IEjariContext>, contractExtra?: Partial<IEjariContractContext> ) {
		const { property } = this.context;
		if ( property ) {
			updateEjariContext( {
				breadcrumbs: [ {
					id: "ejari_details",
					label: LVStrings.ejari_services,
					link: this.props.location.pathname,
				} ],
				activeUserId: getEjariActiveUserId(),
				...ejariExtra
			} );
			let lastContract = getLastContract( property );
			updateContractContext( {
				activeUserId: getEjariActiveUserId(),
				actionDone: false,
				property: property,
				contract: undefined,
				contractNumber: lastContract ? lastContract.ContractNumber : undefined,
				miniContract: lastContract,
				...contractExtra,
			} );
			return true;
		}
		return false;
	}

	updateManagementContractCtx( ejariExtra?: Partial<IEjariContext>, contractExtra?: Partial<IEjariContractContext> ) {
		const { property } = this.context;
		if ( property ) {
			const p: EjariProperty = { ...property, EjariPropertyId: getEjariPropertyId( property ) as any };
			updateEjariContext( {
				breadcrumbs: [ {
					id: "management_contract_services",
					label: LVStrings.ejari_services,
					link: this.props.location.pathname,
				} ],
				activeUserId: getEjariActiveUserId(),
				...ejariExtra
			} );
			updateManagementContractContext( {
				id: property.AssociatedManagementContract?.ManagementContractId,
				properties: [ p ],
				ogProperty: p,
				...contractExtra,
			} );
			return true;
		}
		return false;
	}

	validateProperty = async ( action: TenancyContactAction ) => {
		const { property } = this.context;
		try {
			await validatePropertyForAction( property!, action );
		} catch ( e ) {
			if ( typeof e === "string" ) {
				if ( Strings[ e ] !== undefined ) {
					throw Strings[ e ];
				}
			}
			throw e;
		}
		let propertyID = getPropertyId( property! );
		if ( !propertyID ) {
			propertyID = property?.PropertyId;
		}
		if ( !propertyID ) {
			propertyID = property?.TabuPropertyId;
		}
		const vr: ValidatePropertyRequest = {
			ActiveUserID: this.context.activeUserId!,
			// owner mc is not here
			ActiveUserType: isOwnerLoggedIn() ? 4 : 0,
			ManagementCompanyId: 0,
			OwnerParticipantId: getParticipantID()!,
			PropertyId: propertyID ? propertyID : 0,
			PropertyTypeId: property?.PropertyTypeId!,
			HoldType: property?.IsFreeHold ? 1 : 0,
		};
		return await validatePropertyFromApi( property!, action, vr );
	};

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		if ( !_.isEqual( prevProps.property, this.props.property ) ) {
			this.updateFromContext();
		}
	}

	handleServiceAction = ( id: string ) => {
		const property = this.props.property;
		switch ( id ) {
			case "edit_ejari":
				if ( this.updateEjariCtx() )
					navigate( `edit` );
				break;
			case "print_certificate":
				if ( property ) {
					let lastContract = getLastContract( property );
					downloadTenancyContractByNumber( lastContract?.ContractNumber!, lastContract?.ContractKey! );
				}
				break;
			case "create_new_contract":
				this.doContractAction( TenancyContactAction.NEW_CONTRACT, "ejari/contract/new" );
				break;
			case "renew_contract":
				this.doContractAction( TenancyContactAction.RENEW_CONTRACT, "ejari/contract/renew" );
				break;
			case "terminate_contract":
				resetContractContext();
				if ( this.updateEjariCtx( {}, {
					action: TenancyContactAction.TERMINATE_CONTRACT,
				} ) )
					navigateWithInParent( "mydld", `ejari/contract/terminate` );
				break;
			case "handover_request":
				this.valPropertyHandTakeOver( HTRequestType.HANDOVER );
				break;
			case "takeover_request":
				this.valPropertyHandTakeOver( HTRequestType.TAKEOVER );
				break;
			case "ppty_cooccupant":
				trackEventWithData( "Property", "ManageCoOccupant", {
					tabuPropertyId: property?.TabuPropertyId,
					ejari: true,
				} );
				if ( this.updateEjariCtx() )
					navigate( `cooccupants` );
				break;
		}
	};

	EditService = {
		id: "edit_ejari",
		label: Strings.edit_property_details,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: true,
	};
	DownloadService = {
		id: "print_certificate",
		label: Strings.download_contract,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: true,
	};
	NewContractService = {
		id: "create_new_contract",
		label: Strings.create_new_contract,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
	};
	RenewContractService = {
		id: "renew_contract",
		label: Strings.renew_contract,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
	};
	TerminateContractService: ServiceCard = {
		id: "terminate_contract",
		label: Strings.terminate_contract,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
	};
	HandoverService: ServiceCard = {
		id: "handover_request",
		label: Strings.ejari_handover_request,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
		requiredFeature: "phptRequest",
	};
	TakeoverService: ServiceCard = {
		id: "takeover_request",
		label: Strings.ejari_takeover_request,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
		requiredFeature: "phptRequest",
	};
	PropertyCoOccupant: ServiceCard = {
		id: "ppty_cooccupant",
		label: Strings.manage_co_occupants,
		action: this.handleServiceAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
		requiredFeature: "propertyCoOccupants",
	};

	handleManagementContractAction = ( id: string ) => {
		switch ( id ) {
			case "terminate_management_contract":
				resetManagementContractContext();
				if ( this.updateManagementContractCtx( {}, { action: TenancyContactAction.TERMINATE_CONTRACT } ) ) {
					navigateWithInParent( "mydld", "ejari/management-contract/terminate" );
				}
				break;
			case "renew_management_contract":
				resetManagementContractContext();
				if ( this.updateManagementContractCtx( {}, { action: TenancyContactAction.RENEW_CONTRACT } ) ) {
					navigateWithInParent( "mydld", "ejari/management-contract/renew" );
				}
				break;
			case "create_management_contract":
				resetManagementContractContext();
				this.onValidateMGMContractProperty().then( ( valid ) => {
					if ( valid ) {
						if ( this.updateManagementContractCtx( {}, { action: TenancyContactAction.NEW_CONTRACT } ) ) {
							navigateWithInParent( "mydld", "ejari/management-contract/new" );
						}
					}
				} );
				break;
			case "view_management_contract_requests":
				navigateWithInParent( "mydld", "management_contract_requests" );
				break;
			default:
				break;
		}
	};

	TerminateManagementContract: ServiceCard = {
		id: "terminate_management_contract",
		label: Strings.terminate_management_contract,
		action: this.handleManagementContractAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false,
	};
	RenewManagementContract: ServiceCard = {
		id: "renew_management_contract",
		label: Strings.renew_management_contract,
		action: this.handleManagementContractAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false
	};
	CreateManagementContract: ServiceCard = {
		id: "create_management_contract",
		label: Strings.create_management_contract,
		action: this.handleManagementContractAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false
	};
	ViewManagementContractRequests: ServiceCard = {
		id: "view_management_contract_requests",
		label: Strings.view_mgm_contract_request,
		action: this.handleManagementContractAction,
		descriptionProvider: ServiceDescriptions,
		allowUnverifiedEmailUsers: false
	};

	doContractAction = ( action: TenancyContactAction, dstUrl: string ) => {
		showLoading();
		this.validateProperty( action )
		.then( x => {
			let vp = ( x && x.PropertiesList && x.PropertiesList.length > 0 ) ? x.PropertiesList[ 0 ] : undefined;
			let isCommercial = x ? x.IsCommercialProperty : false;
			let AllowedCoTenants = x ? x.AllowedCoTenants : undefined;
			if ( vp )
				setPropertyId( this.context.property!, vp.EjariPropertyId );
			hideLoading();
			resetContractContext();
			if ( this.updateEjariCtx( { property: this.context.property }, {
				property: this.context.property,
				isCommercial: isCommercial,
				AllowedCoTenants: AllowedCoTenants,
				action: action,
			} ) )
				navigateWithInParent( "mydld", dstUrl );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	onTabChange = ( e: React.MouseEvent ) => {
	e.preventDefault();
	const id = ( e.target as HTMLAnchorElement ).id;
	this.setState({selectedTabId: id});
};

	render() {
		const {
			canDownload, canEdit, canCreateNewContract, canRenewContract, canTerminateContract,
			canHandTakeOver, handTakeOverError,
		} = this.state;
		// const { tabuProperty } = this.context;
		const property = this.props.property;
		if ( property == null )
			return <Redirect to={ "/mydld" }/>;
		const services: ServiceCard[] = [];
		if ( canEdit )
			services.push( this.EditService );
		if ( canDownload )
			services.push( this.DownloadService );
		if ( canCreateNewContract )
			services.push( this.NewContractService );
		if ( canRenewContract )
			services.push( this.RenewContractService );
		if ( canTerminateContract )
			services.push( this.TerminateContractService );
		if ( canHandTakeOver ) {
			services.push( this.HandoverService );
			services.push( this.TakeoverService );
		}

		const isRented = property.IsRented;
		if ( !isRented ) {
			services.push( this.PropertyCoOccupant );
		}
		const { val, lbl } = extractPropInfo( property );

		const managementContractServices: ServiceCard[] = [];
		if ( property?.AssociatedManagementContract ) {
			managementContractServices.push( this.RenewManagementContract );
			managementContractServices.push( this.TerminateManagementContract );
		} else {
			managementContractServices.push( this.CreateManagementContract );
		}
		managementContractServices.push( this.ViewManagementContractRequests );
		const {tabs, selectedTabId} = this.state;
		return <PageContent
			title={ `${ Strings.ejari_services } - ${ lbl } : ${ val }` }
			titleExtra={ <Switch
				id={ "result-unit" }
				onLabel={ Strings.sq_mt }
				offLabel={ Strings.sq_ft }
				value={ this.state.unit.Id === MetricSystem.Id }
				onChange={ isSqm => {
					const update = { unit: ImperialSystem };
					if ( isSqm ) update.unit = MetricSystem;
					this.setState( update, () => {
						dispatch( Actions.UNIT_CHANGED, this.state.unit );
					} );
				} }
			/> }
		>
			<PageTitle title={ Strings.ejari_services } paths={ [ DashboardBCItem ] }/>
            <React.Fragment>
				<EjariDetails
					showTitle={ true }
					property={ property }
                    unit={this.state.unit} />
	            <div className="tabs mydld-property-detail-tab">
		            <nav className="transformer-tabs">
			            <ul>
				            { tabs.map( ( tab: ITab<any> ) => {
					            let name = LocaleUtil.getLocalValue( tab.name );
					            return <li key={ tab.id } className="nav-item">
						            <a
							            href="#" id={ tab.id }
							            className={ `nav-link ${ tab.id === selectedTabId ? "active" : "" }` }
							            onClick={ this.onTabChange }>{ name }</a>
					            </li>;
				            })
				            }
			            </ul>
		            </nav>
		            <div className={ `card mb-4 pb-2 ${ selectedTabId === TabIDs.EJARI_CONTRACT_SERVICES && "active" }` }>
			            <div className="card-body"><EjariContractTab property={property} context={this.context} services={ services }/></div>
		            </div>
		            <div className={ `card mb-4 pb-2 ${ selectedTabId === TabIDs.MGM_CONTRACT_SERVICES && "active" }` }>
			            <div className="card-body"><ManagementContractTab
				            property={ property } context={this.context}
				            services={ managementContractServices }/></div>
		            </div>
	            </div>
	            {!isRented &&
		            <Gated featureId={"propertyCoOccupants"}>
			            <PropertyCoOccupants
				            propertyId={property.TabuPropertyId}
				            propertyTypeId={property.PropertyTypeId}
				            isFreeHold={property.IsFreeHold}
			            />
		            </Gated>
	            }
			</React.Fragment>
			{ handTakeOverError && <ConfirmationPopup
				primaryLabel={ Strings.modify }
				secondaryLabel={ Strings.cancel }
				message={ handTakeOverError }
				onConfirm={ this.onModify }
				onCancel={ this.onHide }/> }
		</PageContent>;
	}

	private updateFromContext() {
		const property = this.context.property;
		const uT = this.context.userType;
		if ( property )
			this.setState( {
				canEdit: canEditProperty( property, uT ),
				canDownload: canDownload( property, uT ) && canDoContractOperations( getLastContract( property )! ),
				canCreateNewContract: canCreateNewContract( property, uT ),
				canRenewContract: canRenewContract( property, uT ),
				canTerminateContract: canTerminateContract( property, uT ),
				canHandTakeOver: canHandTakeOver( property, uT ),
			} );
		return property;
	}

	private valPropertyHandTakeOver( type: HTRequestType ) {
		const property = this.props.property;
		if ( property ) {
			showLoading();
			const req: HTValidatePropertyRequest = {
				IsFreeHold: property.IsFreeHold ? 1 : 0,
				PropertyId: property.PropertyId,
				PropertyTypeId: property.PropertyTypeId,
				ServiceCode: type === HTRequestType.HANDOVER ? EjariServiceCode.HANDOVER : EjariServiceCode.TAKEOVER,
			};
			validateProperty( req ).then( validateResponse ).then( _resp => {
				showHandTakeOver( property, type );
				hideLoading();
			} ).catch( reason => {
				hideLoading();
				if ( reason.codes && reason.codes.length > 0 ) {
					if ( reason.codes.includes( "5" ) || reason.codes.includes( "6" ) ) {
						this.setState( {
							htReqType: type,
							handTakeOverError: reason.message,
						} );
					} else {
						showErrorMessage( reason );
					}
				} else {
					showErrorMessage( reason );
				}
			} );
		}
	}

	private onModify = () => {
		this.setState( {
			handTakeOverError: undefined,
		}, () => {
			this.loadHTRequestDetails();
		} );
	};

	private onHide = () => {
		this.setState( {
			handTakeOverError: undefined,
		} );
	};

	private loadHTRequestDetails() {
		const property = this.props.property;
		const reqType = this.state.htReqType;
		if ( property && reqType ) {
			getRequestDetailsPropertyId( property.PropertyId, reqType )
			.then( validateResponse ).then( ( req: HTRequest ) => {
				setRequest( req );
				hideLoading();
				editPropertyHandTakeOver( req.RequestTypeId );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e );
			} );
		}
	}

	private onValidateMGMContractProperty = (): Promise<boolean> => {
		showLoading();
		const { property } = this.context;
		const properties: ValidateMGMProperty[] = [ {
			EjariPropertyID: getEjariPropertyId( property! ) as any,
			HoldType: Number( property?.IsFreeHold ),
			PropertyId: property?.PropertyId!,
			PropertyTypeId: property?.PropertyTypeId,
		} ];
		const request: ValidateMGMContractRequest = {
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			ManagementCompanyId: 0,
			OwnerParticipantId: isOwnerLoggedIn() ? getParticipantID() : 0,
			Properties: properties,
		};
		return EjariApi.validatePropertiesForMGMContract( request, false )
		.then( resp => {
			hideLoading();
			return !( resp.ValidationErrors && resp.ValidationErrors.length > 0 );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
			return false;
		} );
	};
}

function PropertyWrapper( p: RoutedViewProps ) {
	const ctx = useContext( EjariContext );
	return <PropertyDetails property={ ctx.property } { ...p }/>;
}

export default PropertyWrapper;

function EjariContractTab (props: {property: EjariProperty, context: any, services: any[]}) {
	const {property, context, services} = props;
	return <React.Fragment>
		<MiniContractDetails
			property={ property }
			userType={ context.userType! }
		/>
		<ServiceContainer
			showSearch={ false }
			hideQLinks={ true } title={Strings.ejari_services}
			extraContainerClass={" "}
			services={ services }/>
	</React.Fragment>
}

function ManagementContractTab (props: {property: EjariProperty, context: any, services: any[]}) {
	const {property, context, services} = props;
	return <React.Fragment>
		<MCDetails
			property={ property }
			userType={ context.userType! }
		/>
		<ServiceContainer
			showSearch={ false } extraContainerClass={" "}
			hideQLinks={ true } title={Strings.management_contract_services}
			services={ services }/>
	</React.Fragment>
}

export function EjariDetails( props: {
	property: EjariProperty; unit: UnitSystem, onUnit?: ( isSqm: boolean ) => void, showTitle?: boolean,
	showRentalStatus?: boolean; showUnitSwitch?: boolean;
} ) {
	const { property, unit, onUnit, showTitle, showRentalStatus, showUnitSwitch } = props;
	const isUnit = property.PropertyType === EjariPropertyType.UNIT;
	let dewa = "";
	if ( property.AssociatedDewaAccounts && Array.isArray( property.AssociatedDewaAccounts ) ) {
		dewa = property.AssociatedDewaAccounts.reduce( ( previousValue, currentValue ) => {
			previousValue.push( currentValue.AccountNumber );
			return previousValue;
		}, [] as string[] ).join( "," ).trim();
	}
	if ( StringUtil.isEmptyStringOrNull( dewa ) && property.Dewa && !StringUtil.isEmptyStringOrNull( property.Dewa ) ) {
		dewa = property.Dewa;
	}
	let size = Number( property.Size );
	if ( size ) {
		if ( unit.Id === ImperialSystem.Id ) {
			size = convertToSqFt( Number( property.Size ) );
		}
	}
	return <React.Fragment>
		{ showTitle && <div className="section-title">
			<h5>{ Strings.property_details }</h5>
			{ showUnitSwitch &&
				<div className="flex-floating size-switch">
					<Switch
						id={ "result-unit" }
						onLabel={ Strings.sq_mt }
						offLabel={ Strings.sq_ft }
						value={ unit.Id === MetricSystem.Id }
						onChange={ onUnit }
					/>
				</div> }
		</div> }
		<div className="flex-grid">
			{ showRentalStatus &&
				<DetailsKV
					label={ Strings.property_status.replace( "<br/>", "" ) }
					value={ property.IsRented ? Strings.rented : Strings.vacant }/>
			}
			{ isUnit &&
				<DetailsKV label={ Strings.Unit_Number_Label } value={ property.UnitNo }/>
			}
			<DetailsKV label={ Strings.building_name } value={ getLocalValue( property.BuildingName ) }/>
			<DetailsKV label={ Strings.community } value={ getLocalValue( property.Area ) }/>
			<DetailsKV label={ Strings.municipality_number } value={ property.LandDMNumber }/>
			<DetailsKV
				label={ Strings.usage } value={ property.Usage ? getLocalValue( property.Usage ) :
				getLocalValue( property.EjariUsage ) }/>
			<DetailsKV label={ Strings.size } value={ size }/>
			<DetailsKV label={ Strings.dewa } value={ dewa }/>
			<DetailsKV label={ Strings.property_type } value={ getLocalValue( property.EjariType ) }/>
			<DetailsKV label={ Strings.property_sub_type } value={ getLocalValue( property.EjariSubType ) }/>
			<DetailsKV
				label={ Strings.freehold } value={ property.IsFreeHold ? Strings.yes : Strings.no }
				valueClassName={ `${ property.IsFreeHold ? "yes" : "no" }-label` }/>
		</div>
	</React.Fragment>;
}

function MCDetails( props: { property: EjariProperty; userType: EjariUserType } ) {
	const { property, userType } = props;
	let value = property.AssociatedManagementContract;
	if ( value ) {
		let owner: any = undefined;
		if ( property.Owners && property.Owners.length > 0 ) {
			owner = property.Owners[ 0 ];
		}

		return <React.Fragment>
			<div className="section-title">
				<h5>{ Strings.management_contract }</h5>
			</div>
			<div className="flex-grid">
				<DetailsKV label={ Strings.contract_no } value={ value.McContractNumber }/>
				<DetailsKV label={ Strings.license_source } value={ value.CompanyLicenseNumber }/>
				{ userType === "owner" && <React.Fragment>
					<DetailsKV label={ Strings.company_name_ar } value={ value.CompanyNameAr }/>
					<DetailsKV label={ Strings.company_name_en } value={ value.CompanyNameEn }/>
				</React.Fragment> }
				{ userType === "mc" && owner && <React.Fragment>
					<DetailsKV label={ Strings.owner_name } value={ owner.OwnerName }/>
					<DetailsKV label={ Strings.contract_type } value={ value.ContractType }/>
				</React.Fragment> }
				<DetailsKV label={ Strings.start_date } value={ formatAppDate( value.McStartDate ) }/>
				<DetailsKV label={ Strings.end_date } value={ formatAppDate( value.McEndDate ) }/>
			</div>
		</React.Fragment>;
	} else {
		return null;
	}
}

export function MiniContractDetails( props: { property: EjariProperty; userType: EjariUserType } ) {
	const { property, userType } = props;
	let value = getDisplayableContract( property, userType );
	const paymentUrl = ( value && value.ContractNumber ) ? EjariApi.getEjariPaymentShortUrl( value ) : "";
	const showPayment = ( getManagementUserTypeId() === 0 && value && value.ContractStatus === EjariContractStatus.PENDING ) ? EjariApi.getEjariPaymentShortUrl( value ) : "";
	const paymentHandler = useRef<PayForContract>( null );
	if ( value ) {
		return <React.Fragment>
			<div className="section-title">
				<h5>{ Strings.ejari_contract_details }</h5>
			</div>
			<div className="flex-grid">
				<DetailsKV label={ Strings.contract_number } value={ value.ContractNumber }/>
				<DetailsKV label={ Strings.start_date } value={ formatAppDate( value.ContractStartDate ) }/>
				<DetailsKV label={ Strings.end_date } value={ formatAppDate( value.ContractEndDate ) }/>
				{ value.GraceStartDate &&
					<DetailsKV label={ Strings.grace_start_date } value={ formatAppDate( value.GraceStartDate ) }/> }
				{ value.GraceEndDate &&
					<DetailsKV label={ Strings.grace_end_date } value={ formatAppDate( value.GraceEndDate ) }/> }

				<DetailsKV label={ Strings.tenant_name_ar } value={ value.ContractTenantNameAr }/>
				<DetailsKV label={ Strings.tenant_name_en_new } value={ value.ContractTenantNameEn }/>
				<DetailsKV label={ Strings.rental_amount } value={ toFormattedCurrency( value.ContractAmount! ) }/>
				<DetailsKV label={ Strings.status } value={ value.ContractStatusName }/>
				{ paymentUrl && showPayment &&
					<>
						<DetailsKV
							label={ Strings.payment_url }
							valueRenderer={ () => <span><button
								className={ "btn btn-link p-0" }
								onClick={ () => {
									paymentHandler.current?.initPayment( (
										isSuccess, _data ) => {
										if ( isSuccess ) {
											UpdateProperty( getAppServices().storeService.getStore() );
										}
									} );
								} }>{ paymentUrl }</button></span> }
							value={ paymentUrl }/>
						<PayForContract contractNumber={ value.ContractNumber! } ref={ paymentHandler }/>
					</> }
			</div>
		</React.Fragment>;
	} else {
		return null;
	}
}
